import {
  Alert,
  Box,
  Card,
  Container,
  Typography
} from "@mui/material";
import { styled } from "@mui/material/styles";
import { authAPI, LoginResponse } from "api/auth";
import { useState } from "react";
import { useDispatch } from "react-redux";
import { useHistory } from "react-router-dom";
import { SET_USER_INFO } from "store/actionTypes/auth";
import { storeAccessToken } from "utils/auth";
import { CodeRequest, CodeVerification, ForgotPassword, PasswordLogin, ResetPassword } from './components';

const PREFIX = 'LoginPage';

const classes = {
  card: `${PREFIX}-card`,
  form: `${PREFIX}-form`,
  microsoftButton: `${PREFIX}-microsoftButton`,
  codeButton: `${PREFIX}-codeButton`,
  dividerContainer: `${PREFIX}-dividerContainer`,
  dividerText: `${PREFIX}-dividerText`,
  switchMode: `${PREFIX}-switchMode`
};

const StyledContainer = styled(Container)(() => ({
  [`& .${classes.card}`]: {
    maxWidth: 400,
    margin: "0 auto",
    padding: "2rem"
  },
  [`& .${classes.form}`]: {
    display: "flex",
    flexDirection: "column",
    gap: "1rem"
  },
  [`& .${classes.microsoftButton}`]: {
    backgroundColor: "#0078d4",
    color: "white",
    textTransform: "none",
    "&:hover": {
      backgroundColor: "#106ebe"
    }
  },
  [`& .${classes.codeButton}`]: {
    backgroundColor: "#28a745",
    color: "white",
    textTransform: "none",
    "&:hover": {
      backgroundColor: "#218838"
    }
  },
  [`& .${classes.dividerContainer}`]: {
    display: "flex",
    alignItems: "center",
    margin: "1rem 0"
  },
  [`& .${classes.dividerText}`]: {
    margin: "0 1rem",
    color: "#666",
    fontSize: "0.875rem"
  },
  [`& .${classes.switchMode}`]: {
    color: "#0078d4",
    cursor: "pointer",
    textDecoration: "underline",
    "&:hover": {
      color: "#106ebe"
    }
  }
}));

export const Signin = () => {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [code, setCode] = useState("");
  const [loading, setLoading] = useState(false);
  const [codeLoading, setCodeLoading] = useState(false);
  const [loginMode, setLoginMode] = useState<'password' | 'code' | 'verify-code' | 'forgot-password' | 'reset-password'>('password');
  const [message, setMessage] = useState<{ type: 'success' | 'error', text: string } | null>(null);
  const [codeEmail, setCodeEmail] = useState("");
  const [resetCode, setResetCode] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const dispatch = useDispatch();
  const history = useHistory();

  const handleUsernamePasswordLogin = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    setMessage(null);

    try {
      const response = await authAPI.login({ email, password });
      handleLoginSuccess(response);

    } catch (error: any) {
      console.error("Login failed:", error);
      setMessage({
        type: 'error',
        text: error.response?.data?.message || ('Login failed. Please check your credentials and try again.')
      });
    } finally {
      setLoading(false);
    }
  };

  const handleRequestCode = async (e: React.FormEvent) => {
    e.preventDefault();
    setCodeLoading(true);
    setMessage(null);

    try {
      const response = await authAPI.requestCode({ email: codeEmail });
      console.log("Code request successful:", response);

      setMessage({
        type: 'success',
        text: response.message || ('Verification code sent to your email. Please check your inbox.')
      });
      setLoginMode('verify-code');
    } catch (error: any) {
      console.error("Code request failed:", error);
      setMessage({
        type: 'error',
        text: error.response?.data?.message || ('Failed to send verification code. Please try again.')
      });
    } finally {
      setCodeLoading(false);
    }
  };

  const handleVerifyCode = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    setMessage(null);

    try {
      const response = await authAPI.verifyCode({ email: codeEmail, loginCode: code });
      handleLoginSuccess(response);
    } catch (error: any) {
      console.error("Code verification failed:", error);
      setMessage({
        type: 'error',
        text: error.response?.data?.message || ('Invalid verification code. Please try again.')
      });
    } finally {
      setLoading(false);
    }
  };

  const resetToPasswordMode = () => {
    setLoginMode('password');
    setMessage(null);
    setCode("");
    setCodeEmail("");
    setResetCode("");
    setNewPassword("");
    setConfirmPassword("");
  };

  const handleForgotPassword = async (e: React.FormEvent) => {
    e.preventDefault();
    setCodeLoading(true);
    setMessage(null);

    try {
      const response = await authAPI.forgotPassword({ email: codeEmail });
      console.log("Forgot password request successful:", response);

      setMessage({
        type: 'success',
        text: response.message || ('Password reset code sent to your email. Please check your inbox.')
      });
      setLoginMode('reset-password');
    } catch (error: any) {
      console.error("Forgot password request failed:", error);
      setMessage({
        type: 'error',
        text: error.response?.data?.message || ('Failed to send password reset code. Please try again.')
      });
    } finally {
      setCodeLoading(false);
    }
  };

  const handleResetPassword = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    setMessage(null);

    try {
      const response = await authAPI.resetPassword({
        email: codeEmail,
        verificationCode: resetCode,
        newPassword,
      });
      console.log("Password reset successful:", response);

      setMessage({
        type: 'success',
        text: response.message || ('Password reset successfully! You can now sign in with your new password.')
      });

      // Reset form and go back to login after success
      setTimeout(() => {
        resetToPasswordMode();
      }, 2000);

    } catch (error: any) {
      console.error("Password reset failed:", error);
      setMessage({
        type: 'error',
        text: error.response?.data?.message || ('Failed to reset password. Please check your code and try again.')
      });
    } finally {
      setLoading(false);
    }
  };

  const handleLoginSuccess = (response: LoginResponse) => {
    if (!response.data.user.isAdmin) {
      setMessage({
        type: 'error',
        text: ('You do not have admin access.')
      });
      return;
    }
    storeAccessToken(response);
    dispatch({ type: SET_USER_INFO, payload: response.data.user });

    setMessage({
      type: 'success',
      text: ('Login successful! Redirecting...')
    });

    // Redirect after a short delay
    setTimeout(() => {
      history.replace('/'); // Use history.replace to avoid going back to login on back button
    }, 1000);
  }

  return (
    <Box 
      sx={{ backgroundImage: 'url(/images/auth-bg.jpg)',
        backgroundSize: 'cover',
        py: { sm: 4, xs: 2 },
        display: 'flex',
        alignItems: 'center',
        minHeight: '100vh'}}>
      <StyledContainer>
        <Card className={classes.card}>
          <Typography variant="h4" align="center" marginBottom={2} fontWeight={600} color="secondary">
            {loginMode === 'code' ? ("Request Login Code") :
              loginMode === 'verify-code' ? ("Enter Verification Code") :
                loginMode === 'forgot-password' ? ("Forgot Password") :
                  loginMode === 'reset-password' ? ("Reset Password") :
                    ("Sign In")}
          </Typography>

          {message && (
            <Alert severity={message.type} sx={{ mb: 2 }}>
              {message.text}
            </Alert>
          )}

          {loginMode === 'password' && (
            <>
              <PasswordLogin
                email={email}
                password={password}
                loading={loading}
                onEmailChange={setEmail}
                onPasswordChange={setPassword}
                onSubmit={handleUsernamePasswordLogin}
                onSwitchToCode={() => setLoginMode('code')}
                onForgotPassword={() => {
                  setCodeEmail(email); // Pre-fill email if already entered
                  setLoginMode('forgot-password');
                }}
                formClassName={classes.form}
                switchModeClassName={classes.switchMode}
              />
            </>
          )}

          {loginMode === 'code' && (
            <CodeRequest
              codeEmail={codeEmail}
              codeLoading={codeLoading}
              onEmailChange={setCodeEmail}
              onSubmit={handleRequestCode}
              onBackToPassword={resetToPasswordMode}
              formClassName={classes.form}
              codeButtonClassName={classes.codeButton}
              switchModeClassName={classes.switchMode}
            />
          )}

          {loginMode === 'verify-code' && (
            <CodeVerification
              code={code}
              codeEmail={codeEmail}
              loading={loading}
              onCodeChange={setCode}
              onSubmit={handleVerifyCode}
              onResendCode={() => setLoginMode('code')}
              onBackToPassword={resetToPasswordMode}
              formClassName={classes.form}
              switchModeClassName={classes.switchMode}
            />
          )}

          {loginMode === 'forgot-password' && (
            <ForgotPassword
              email={codeEmail}
              loading={codeLoading}
              onEmailChange={setCodeEmail}
              onSubmit={handleForgotPassword}
              onBackToLogin={resetToPasswordMode}
              formClassName={classes.form}
              codeButtonClassName={classes.codeButton}
              switchModeClassName={classes.switchMode}
            />
          )}

          {loginMode === 'reset-password' && (
            <ResetPassword
              resetCode={resetCode}
              newPassword={newPassword}
              confirmPassword={confirmPassword}
              email={codeEmail}
              loading={loading}
              onResetCodeChange={setResetCode}
              onNewPasswordChange={setNewPassword}
              onConfirmPasswordChange={setConfirmPassword}
              onSubmit={handleResetPassword}
              onResendCode={() => setLoginMode('forgot-password')}
              onBackToLogin={resetToPasswordMode}
              formClassName={classes.form}
              switchModeClassName={classes.switchMode}
            />
          )}
        </Card>
      </StyledContainer>
    </Box>
  );
};